/**
 * Art for the logo easter egg: the nine enemy sprites (pixel art), the
 * WarCarp mark split into the parts the reactions animate, and the other
 * carp that turn up in the gags (a pirate rival and a lady carp).
 *
 * Enemies are pixel art on purpose: they are the "invaders", so they read as
 * a different world from the flat vector carp, and their explosion is literally
 * their own pixels flying apart, Space Invaders style. Each is a flat
 * silhouette with cut-out details, like the mark, in a muted field palette
 * with one strong colour each so the nine stay distinct in both themes.
 */
import { BRAND_COLORS, BRAND_MARKS } from '@warcarp/ui'
import type { LogoEnemy } from './logoEasterEgg'

export interface EnemyArt {
  name: string
  /** Rows of cells; `.` is empty, other letters index `palette`. Faces left (toward the carp). */
  grid: readonly string[]
  palette: Record<string, string>
  /** Debris colours for the explosion (primary first). */
  burst: readonly [string, string]
  blurb: string
}

export const ENEMY_ART: Record<LogoEnemy, EnemyArt> = {
  puffer: {
    name: 'Puff', blurb: 'A pufferfish that bobs in slowly and inflates when hit.',
    palette: { a: '#d9a33a', b: '#f3e3b3', c: '#9c6f1f', k: '#18170f', w: '#fffaf0' },
    burst: ['#d9a33a', '#f3e3b3'],
    grid: [
      '.....c..c.c...',
      '..c.aaaaaa..c.',
      '...aaaaaaaaa..',
      '.caaaaaaaaaa.c',
      '.aawkaaaaaaacc',
      'caakkaaaaaaacc',
      '.kaaaaaaaaaac.',
      'ckbbbbbbbbaa.c',
      '..bbbbbbbbba..',
      '.c.bbbbbbba.c.',
      '....c.c..c....',
    ],
  },
  jelly: {
    name: 'Jelly', blurb: 'A jellyfish that pulses down from above.',
    palette: { a: '#c45fa0', b: '#e8a6cf', k: '#3b1430', w: '#fff3fa' },
    burst: ['#c45fa0', '#e8a6cf'],
    grid: [
      '....aaaa....',
      '..aaaaaaaa..',
      '.aabaaaaaaa.',
      'aabaaaaaaaaa',
      'aawkaaaawkaa',
      'aakkaaaakkaa',
      'aaaaakkaaaaa',
      'abababababab',
      '.b..b..b..b.',
      'b..b..b..b..',
      '.b..b..b..b.',
      '..b..b..b..b',
    ],
  },
  shark: {
    name: 'Rival shark', blurb: 'The rival shark: a fast dash with a show-off stop.',
    palette: { a: '#5f7488', b: '#d4dbe0', c: '#3e4e5e', k: '#18170f', w: '#ffffff' },
    burst: ['#5f7488', '#d4dbe0'],
    grid: [
      '..........c.......',
      '.........cc.......',
      '........caa.......',
      '....aaaaaaaaa...cc',
      '..aaaaaaaaaaaaa.ca',
      '.awkaaaaaacaaaaaa.',
      'aakkaaaaaaacaaaaa.',
      'wbwbwbbbbbbbbaa.ca',
      '.bbbbbbbbbbbaa..cc',
      '....bb.....c......',
    ],
  },
  angler: {
    name: 'Angler', blurb: 'An anglerfish that creeps in behind its lure.',
    palette: { a: '#2f6266', b: '#5e9496', c: '#1d3d40', k: '#0e1a1b', w: '#f4f1e0', l: '#ffd84a' },
    burst: ['#2f6266', '#ffd84a'],
    grid: [
      'll.............',
      'll.............',
      '..c............',
      '...cc.aaaaa....',
      '....aaaaaaaa.cc',
      '...aawkaaaaaacc',
      '..aaakkaaaaaaac',
      '.wawawabbbbaacc',
      '.waaaaabbbbba.c',
      '..wawabbbbba...',
      '....aaaaaaa....',
    ],
  },
  crab: {
    name: 'Pinch', blurb: 'A crab that scuttles in sideways, claws up.',
    palette: { a: '#d4502a', b: '#f08a5d', k: '#18170f', w: '#fff6ee' },
    burst: ['#d4502a', '#f08a5d'],
    grid: [
      '.aa.........aa.',
      'a.aa.......aa.a',
      'aaa.........aaa',
      '.a..w.....w..a.',
      '..a.k.....k.a..',
      '...aaaaaaaaa...',
      '..aaabaaabaaa..',
      '.aabbbbbbbbbaa.',
      'a..a..a.a..a..a',
      '..a..a...a..a..',
    ],
  },
  squid: {
    name: 'Inky', blurb: 'A squid that jets in by fits and starts and goes out in a cloud of ink.',
    palette: { a: '#6c5bc4', b: '#b3a6ee', k: '#18170f', w: '#ffffff' },
    burst: ['#6c5bc4', '#b3a6ee'],
    grid: [
      '.....aa.....',
      '....aaaa....',
      '...aaaaaa...',
      '.baaaaaaaab.',
      'bbaaaaaaaabb',
      '..aaaaaaaa..',
      '..awkaawka..',
      '..akkaakka..',
      '..aaaaaaaa..',
      '.a.a.aa.a.a.',
      'a.a..aa..a.a',
      '.a..a..a..a.',
      'a...a..a...a',
    ],
  },
  moray: {
    name: 'Moray', blurb: 'A moray eel that snakes up from below, jaws snapping.',
    palette: { a: '#a59a2e', b: '#e2db86', c: '#5f5916', k: '#18170f', w: '#fffaf0' },
    burst: ['#a59a2e', '#e2db86'],
    grid: [
      '.aaaa.............',
      'aawkaa.......cccc.',
      'aakkaaa....ccaaaac',
      'w.w.aaaacccaabaaa.',
      '....aaaaaaaaaaab.a',
      'w.w.aabaaaaaaaaaa.',
      'aaaaaaaaba...aaa..',
      '.aaaaaa...........',
    ],
  },
  urchin: {
    name: 'Spike', blurb: 'A sea urchin that rolls in and bristles all over.',
    palette: { a: '#34407a', b: '#8fa6e8', k: '#0d1024', w: '#ffffff' },
    burst: ['#34407a', '#8fa6e8'],
    grid: [
      '......b......',
      '.b....b....b.',
      '..b..aaa..b..',
      '...aaaaaaa...',
      '..aaaaaaaaa..',
      '.aawkaaawkaa.',
      'bbakkaaakkabb',
      '.aaaaaaaaaaa.',
      '..aaakkkaaa..',
      '...aaaaaaa...',
      '..b..aaa..b..',
      '.b....b....b.',
      '......b......',
    ],
  },
  seahorse: {
    name: 'Trotter', blurb: 'A seahorse that gallops in and rears up.',
    palette: { a: '#3fa37a', b: '#a8e6c6', c: '#e8508a', k: '#18170f', w: '#ffffff' },
    burst: ['#3fa37a', '#a8e6c6'],
    grid: [
      '....cc....',
      '...aaaa...',
      'aaaawkab..',
      'aaa.kkab..',
      '....aaab..',
      '...aaabb.c',
      '...aaab.cc',
      '..aaabb..c',
      '..aaab....',
      '...aab....',
      '....aab...',
      '..a..ab...',
      '..aa.ab...',
      '...aaa....',
    ],
  },
}

/** Grid cells grouped by colour, as one SVG path per colour (cheap to draw). */
export function pixelPaths(grid: readonly string[], palette: Record<string, string>) {
  const byColour = new Map<string, string[]>()
  grid.forEach((row, y) => [...row].forEach((cell, x) => {
    const colour = palette[cell]
    if (!colour) return
    if (!byColour.has(colour)) byColour.set(colour, [])
    byColour.get(colour)!.push(`M${x} ${y}h1.03v1.03h-1.03z`)
  }))
  return [...byColour].map(([fill, cells]) => ({ fill, d: cells.join('') }))
}

/** 2x2 debris chunks of a sprite: centre (grid units) and colour of each. */
export function pixelChunks(grid: readonly string[], palette: Record<string, string>) {
  const chunks: { x: number; y: number; colour: string }[] = []
  for (let y = 0; y < grid.length; y += 2) for (let x = 0; x < grid[0]!.length; x += 2) {
    const cells = [grid[y]?.[x], grid[y]?.[x + 1], grid[y + 1]?.[x], grid[y + 1]?.[x + 1]].map(cell => (cell && palette[cell]) || null).filter(Boolean) as string[]
    if (cells.length >= 2) chunks.push({ x: x + 1, y: y + 1, colour: cells[0]! })
  }
  return chunks
}

/** The classic invader hit glyph, flashed where an enemy was. */
export const INVADER_BURST = [
  'k...k...k',
  '.k..k..k.',
  '..k...k..',
  'kk.....kk',
  '..k...k..',
  '.k..k..k.',
  'k...k...k',
] as const

// ---- The carp, split into parts --------------------------------------------

/** The mark's viewBox (the logo's fish shares it: same origin and height). */
export const FISH_VIEWBOX = { x: 0.75, y: 4.32, width: 22.5, height: 15.36 } as const
export const FISH_ASPECT = BRAND_MARKS.mark.aspect
/** Fish-art units (before the mark's `translate(-0.75 1.07) scale(0.87649)`) to viewBox units. */
const ART = { dx: -0.75, dy: 1.07, scale: 0.87649 }
function artPoint(x: number, y: number) {
  return { x: x * ART.scale + ART.dx - FISH_VIEWBOX.x - FISH_VIEWBOX.width / 2, y: y * ART.scale + ART.dy - FISH_VIEWBOX.y - FISH_VIEWBOX.height / 2 }
}
/** Anchor points on the carp, in viewBox units from the mark's centre. */
export const FISH_POINTS = {
  muzzle: artPoint(27.1, 18.55),
  mouth: artPoint(23.6, 12),
  eye: artPoint(18.65, 10.5),
  tail: artPoint(2, 12),
  /** The helmet's own box centre (see HELMET_BOX). */
  helmet: { x: 15 - FISH_VIEWBOX.x - FISH_VIEWBOX.width / 2, y: 7.4 - FISH_VIEWBOX.y - FISH_VIEWBOX.height / 2 },
} as const
/** The helmet sprite's own box (viewBox units), so it tips around its own centre. */
export const HELMET_BOX = { x: 11.8, y: 5.6, width: 6.4, height: 3.6 } as const

const ART_TRANSFORM = 'translate(-0.75 1.07) scale(0.87649)'
/** The header's paper colour per theme (apps/mobile/lib/themeVars.ts). */
const HALO_COLORS = { light: '#f4ecd3', dark: '#2c3520' } as const
/** The mark's fish shapes (fish-art units): body, fin, rifle, trigger guard. */
const SILHOUETTE = [
  'M6 10 L2 6 L2 18 L6 14 C8.2 17 12.6 19 17 17 L20.3 16 L23.6 12 L20.3 8 L17 7 C12.6 5 8.2 7 6 10 Z',
  'M8.2 7 L11.5 4 L17 6 Z',
  'M7.65 17.9 L11.85 17.9 L11.85 19.2 L10.65 20.95 L7.65 20.95 Z',
  'M10.65 17.9 L27.1 17.9 L27.1 19.2 L10.65 19.2 Z',
  'M9.48 14.95 L13.18 17.55 L11.68 19.75 Z',
] as const
const HELMET_PATH = 'M14.62 8.1 C14.62 5.4 19.82 5.4 19.82 8.1 L21.22 8.3 L21.22 9.1 L14.62 9.1 Z'
export type FishPart = 'eyelid' | 'bare' | 'helmet' | 'halo' | 'haloBare'

function wrap(inner: string, box: { x: number; y: number; width: number; height: number } = FISH_VIEWBOX) {
  const { x, y, width, height } = box
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${x} ${y} ${width} ${height}">${inner}</svg>`
}

/**
 * - `eyelid`: covers the eye with a shut "^" eye, for winks and squints. Laid
 *   over the full mark, so fading it never makes the body see-through.
 * - `bare`: the carp with its helmet off (and the helmet's outline cut closed).
 * - `helmet`: the helmet alone.
 * - `halo` / `haloBare` (no helmet): the whole silhouette, outlined, in the page colour: a "sticker"
 *   edge drawn under the enlarged carp so it reads cleanly over the wordmark.
 */
export function fishPartXml(part: FishPart, variant: 'light' | 'dark', id: string): string {
  const fish = BRAND_COLORS[variant].fish
  if (part === 'helmet') return wrap(`<g fill="${fish}" transform="${ART_TRANSFORM}"><path d="${HELMET_PATH}"/></g>`, HELMET_BOX)
  if (part === 'halo' || part === 'haloBare') {
    const paper = HALO_COLORS[variant]
    const shapes = part === 'halo' ? [...SILHOUETTE, HELMET_PATH] : SILHOUETTE
    return wrap(`<g fill="${paper}" stroke="${paper}" stroke-width="2.2" stroke-linejoin="round" transform="${ART_TRANSFORM}">${shapes.map(d => `<path d="${d}"/>`).join('')}</g>`)
  }
  if (part === 'eyelid') {
    return wrap(`<defs><mask id="${id}-lid" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><g transform="${ART_TRANSFORM}">`
      + `<rect x="-50" y="-50" width="200" height="200" fill="#fff"/>`
      + `<path d="${HELMET_PATH}" fill="#000" stroke="#000" stroke-width="1.71" stroke-linejoin="round"/>`
      + `<path d="M17.55 10.95 Q18.65 9.75 19.75 10.95" fill="none" stroke="#000" stroke-width="0.55" stroke-linecap="round"/>`
      + `</g></mask></defs><g mask="url(#${id}-lid)"><g fill="${fish}" transform="${ART_TRANSFORM}"><circle cx="18.65" cy="10.5" r="1.08"/></g></g>`)
  }
  return bareFishXml(variant, id)
}

/** The mark without its helmet: drop the helmet group and its outline cut. */
export function bareFishXml(variant: 'light' | 'dark', id: string) {
  const helmetCut = new RegExp(`<path d="${HELMET_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}" fill="#000"[^>]*/>`)
  const helmetGroup = new RegExp(`<g fill="\\{\\{FISH\\}\\}" transform="[^"]*"><path d="${HELMET_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"/></g>`)
  return BRAND_MARKS.mark.svg
    .replace(helmetCut, '')
    .replace(helmetGroup, '')
    .replace(/wc-mark-/g, `${id}-mark-`)
    .replace(/\{\{FISH\}\}/g, BRAND_COLORS[variant].fish)
}

/** The other carp in the gags: a pirate rival (eyepatch) and a lady carp (lashes and a bow). */
export type CarpLook = 'rival' | 'lady'
const CARP_LOOKS: Record<CarpLook, { body: string; extra: string }> = {
  rival: {
    body: '#7b2d26',
    extra: '<path d="M16 8.2 L21.6 12.4" stroke="#18170f" stroke-width="0.5"/><circle cx="18.65" cy="10.5" r="1.45" fill="#18170f"/>',
  },
  lady: {
    body: '#e2789f',
    extra: '<circle cx="18.65" cy="10.5" r="1.15" fill="#fffaf0"/><circle cx="18.95" cy="10.5" r="0.6" fill="#18170f"/>'
      + '<path d="M17.6 9.5 L17.1 8.6 M18.6 9.3 L18.6 8.3 M19.6 9.5 L20.1 8.6" stroke="#18170f" stroke-width="0.35" stroke-linecap="round"/>'
      + '<path d="M12.6 5.4 L10.4 4 L10.6 6.9 Z M12.6 5.4 L14.8 4 L14.6 6.9 Z" fill="#d62f5f"/><circle cx="12.6" cy="5.4" r="0.8" fill="#ff8fb0"/>'
      + '<path d="M23.2 12.6 Q22.4 13.4 21.7 12.9" stroke="#b0123f" stroke-width="0.6" fill="none" stroke-linecap="round"/>',
  },
}
/** Another carp: the mark's body and fin (no rifle, no helmet), in its own colour and look. Faces right like the logo. */
export function carpXml(look: CarpLook) {
  const { body, extra } = CARP_LOOKS[look]
  return wrap(`<g transform="${ART_TRANSFORM}"><path d="${SILHOUETTE[0]}" fill="${body}"/><path d="${SILHOUETTE[1]}" fill="${body}"/>`
    + `${extra}</g>`)
}

/**
 * Score and combo text ("+100", "POP!", "x3 COMBO!") sits on this chip: a
 * near-black pill with a cream outline and bright yellow lettering. The dark
 * fill carries it over light paper and the red New Game button, the cream
 * edge over the dark theme's canvas, so it reads in both themes.
 */
export const SCORE_CHIP = { fill: '#1b1a12', edge: '#fff6d6', text: '#ffe066' } as const
